import type {
  U340xKpiMonthDetailRow,
  U340xKpiMonthDetailTotals,
} from '../types/u340xKpiLuTypes';

export type U340xKpiDetailBillableFilter = 'all' | 'billable' | 'nonBillable';

export type U340xKpiDetailNatureFilter = 'all' | 'standard' | 'writeOff';

function toNumber(value: string | number | boolean | null | undefined): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

/** Totaux recalculés sur les lignes affichées (filtre actif). */
export function u340xSumDetailTotalsFromRows(
  rows: U340xKpiMonthDetailRow[],
): U340xKpiMonthDetailTotals {
  let billableQuantity = 0;
  let nonBillableQuantity = 0;
  let billableCostAmount = 0;
  let nonBillableCostAmount = 0;
  let invoiceCount = 0;
  let amountHt = 0;
  let amountTec = 0;
  let writeOffInvoiceCount = 0;
  let writeOffAmountHt = 0;
  let writeOffAmountTec = 0;
  for (const row of rows) {
    billableQuantity += toNumber(row.billableQuantity);
    nonBillableQuantity += toNumber(row.nonBillableQuantity);
    billableCostAmount += toNumber(row.billableCostAmount);
    nonBillableCostAmount += toNumber(row.nonBillableCostAmount);
    const rowAmount = toNumber(row.amountHt);
    const rowTec = toNumber(row.amountTec);
    if (row.documentNature === 'WIP_WRITE_OFF') {
      writeOffInvoiceCount += 1;
      writeOffAmountHt += rowAmount;
      writeOffAmountTec += rowTec;
    } else if (row.documentNature === 'STANDARD') {
      invoiceCount += 1;
      amountHt += rowAmount;
      amountTec += rowTec;
    } else if (row.amountHt !== undefined && row.amountHt !== null) {
      invoiceCount += 1;
      amountHt += rowAmount;
      amountTec += rowTec;
    }
  }
  return {
    billableQuantity,
    nonBillableQuantity,
    billableCostAmount,
    nonBillableCostAmount,
    invoiceCount,
    amountHt,
    amountTec,
    writeOffInvoiceCount,
    writeOffAmountHt,
    writeOffAmountTec,
  };
}

export function u340xFilterDetailRowsByBillable(
  rows: U340xKpiMonthDetailRow[],
  filter: U340xKpiDetailBillableFilter,
): U340xKpiMonthDetailRow[] {
  if (filter === 'all') return rows;
  if (filter === 'billable') return rows.filter((row) => row.billable === true);
  return rows.filter((row) => row.billable === false);
}

export function u340xFilterDetailRowsByNature(
  rows: U340xKpiMonthDetailRow[],
  filter: U340xKpiDetailNatureFilter,
): U340xKpiMonthDetailRow[] {
  if (filter === 'all') return rows;
  if (filter === 'standard') {
    return rows.filter((row) => row.documentNature === 'STANDARD');
  }
  return rows.filter((row) => row.documentNature === 'WIP_WRITE_OFF');
}

function formatQuantity(value: number): string {
  return new Intl.NumberFormat('fr-CA', { maximumFractionDigits: 2 }).format(value);
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('fr-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 2,
  }).format(value);
}

/** Montant HT affiché (standard + write-off) pour la colonne amountHt. */
export function u340xCombinedAmountHt(totals: U340xKpiMonthDetailTotals): number {
  return totals.amountHt + totals.writeOffAmountHt;
}

/** Montant TEC affiché (standard + write-off) pour la colonne amountTec. */
export function u340xCombinedAmountTec(totals: U340xKpiMonthDetailTotals): number {
  return totals.amountTec + totals.writeOffAmountTec;
}

/** Valeur formatée du total pour une colonne détail (tfoot), ou null si hors métriques. */
export function u340xFormatTotalForColumn(
  columnKey: string,
  totals: U340xKpiMonthDetailTotals,
): string | null {
  switch (columnKey) {
    case 'billableQuantity':
      return formatQuantity(totals.billableQuantity);
    case 'nonBillableQuantity':
      return formatQuantity(totals.nonBillableQuantity);
    case 'billableCostAmount':
      return formatCurrency(totals.billableCostAmount);
    case 'nonBillableCostAmount':
      return formatCurrency(totals.nonBillableCostAmount);
    case 'amountHt':
      return formatCurrency(u340xCombinedAmountHt(totals));
    case 'amountTec':
      return formatCurrency(u340xCombinedAmountTec(totals));
    default:
      return null;
  }
}
